<template>
  <div class="devices-container">
    <toastification-loading-content ref="loadingToast" />

    <!-- Usuarios del panel -->
    <h5 class="mb-1">{{ $t('operational_security.operational_processes.technicians') }}</h5>

    <b-row class="mb-2">
      <b-col cols="12" md="6">
        <!-- Usuario tecnico del panel -->
        <validation-provider
          #default="validationContext"
          :name="$t('operational_security.operational_processes.panel_users')"
        >
          <b-form-group
            :label="$t('operational_security.operational_processes.panel_users')"
            label-for="panel_users"
            :description="
              isLoadingPanelUsers
                ? null
                : panelUsersOptions.length
                ? null
                : $t('operational_security.operational_processes.panel_users_empty')
            "
          >
            <v-select
              id="panel_users"
              :value="formData.panel_user"
              @input="$emit('form-data-changed', { key: 'panel_user', value: $event })"
              :options="panelUsersOptions"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :disabled="isDisabledForm"
              :loading="isLoadingPanelUsers"
              :placeholder="
                $t('operational_security.operational_processes.panel_users_placeholder')
              "
            />
            <b-form-invalid-feedback :state="getValidationState(validationContext)">
              {{ validationContext.errors[0] }}
            </b-form-invalid-feedback>
          </b-form-group>
        </validation-provider>
      </b-col>
      <b-col cols="12" md="6">
        <!-- Codigo de usuario -->
        <validation-provider
          #default="validationContext"
          :name="$t('operational_security.operational_processes.panel_user_code')"
          :rules="formData.panel_user ? 'required' : ''"
        >
          <b-form-group
            :label="$t('operational_security.operational_processes.panel_user_code')"
            label-for="panel_user_code"
          >
            <b-input-group>
              <b-form-input
                id="panel_user_code"
                v-model="formData.panel_user_code"
                :placeholder="
                  $t(
                    'operational_security.operational_processes.panel_user_code_placeholder'
                  )
                "
                readonly
              />
              <b-input-group-append>
                <b-button
                  variant="outline-primary"
                  @click="updatePanelUserCode()"
                  class="padding-0-1"
                  title="Generar código"
                >
                  <b-spinner v-if="isCreatingCode" small />
                  <feather-icon v-else icon="RotateCwIcon" size="16" />
                </b-button>
              </b-input-group-append>
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-input-group>
          </b-form-group>
        </validation-provider>
      </b-col>
    </b-row>

    <!-- Dispositivos del panel -->
    <h5 class="mb-1">{{ $t('operational_security.operational_processes.devices') }}</h5>

    <!-- Search + Sync -->
    <b-row class="justify-content-between mb-1 mx-0 align-items-center">
      <b-col cols="12" class="px-0">
        <div class="d-flex align-items-center justify-content-end">
          <b-form-input
            v-model="textFilter"
            :placeholder="$t('table.search')"
            class="mr-1 form-control-sm"
          />
          <b-button
            variant="primary"
            @click="syncDevices()"
            :disabled="loading"
            class="btn-sm"
            v-if="$can('sync_devices', 'DmpPanel')"
          >
            <span class="text-nowrap text-capitalize">
              {{ $t("dmp.syncDevices") }}
            </span>
          </b-button>
        </div>
      </b-col>
    </b-row>

    <!-- Heading table -->
    <div>
      <b-row class="text-muted p-1 mx-0 table-fields-devices">
        <b-col class="table-title">ACCIONES</b-col>
        <b-col class="table-title">ESTADO</b-col>
        <b-col class="table-title">TIPO</b-col>
        <b-col class="table-title">NOMBRE</b-col>
        <b-col class="table-title">ID DE DISPOSITIVO</b-col>
        <b-col class="table-title">Recordatorio</b-col>
        <b-col class="table-title"></b-col>
      </b-row>

      <!-- Area accordion -->
      <div v-if="panel.devices && panel.devices.areas">
        <div
          v-for="area in filteredAreas"
          :key="`area-${area.id}`"
          class="area-accordion"
        >
          <!-- Area header (clickable) -->
          <div
            class="area-header device-row cursor-pointer"
            @click="toggleArea(area.id)"
            :class="{ 'area-expanded': expandedAreas.includes(area.id) }"
          >
            <b-row class="align-items-center p-2">
              <b-col class="pr-0">
                <div class="d-flex align-items-center">
                  <i
                    class="feather mr-1 transition-all"
                    :class="
                      expandedAreas.includes(area.id)
                        ? 'icon-chevron-down'
                        : 'icon-chevron-right'
                    "
                  ></i>
                  <dmp-area
                    :device="area"
                    :trans="trans"
                    :loadingToast="loadingToast"
                    :updateStatus="updateStatus"
                    :maintenance-value="
                      isInMaintenance('area', area.id, area.device_proxy_id)
                    "
                    :maintenance-zone-count="getAreaMaintenanceCount(area)"
                    :total-zone-count="area.devices ? area.devices.length : 0"
                    @maintenance-toggle="handleMaintenanceToggle"
                  />
                </div>
              </b-col>
              <b-col>
                <span :class="getStatusClass(area.status)">
                  {{ area.status }}
                </span>
              </b-col>
              <b-col>{{ area.device_type.toUpperCase() }}</b-col>
              <b-col>{{ area.text }}</b-col>
              <b-col>{{ area.id }}</b-col>
              <b-col></b-col>
              <b-col></b-col>
            </b-row>
          </div>

          <!-- Collapsible content with the area's zones -->
          <b-collapse :visible="expandedAreas.includes(area.id)" class="area-content">
            <div class="zones-container border-left">
              <div
                v-for="zone in area.devices"
                :key="`zone-${zone.id}`"
                class="device-row zone-row border-bottom"
              >
                <b-row class="align-items-center p-2">
                  <b-col>
                    <dmp-zone
                      :device="zone"
                      :trans="trans"
                      :loadingToast="loadingToast"
                      :updateStatus="updateStatus"
                      :maintenance-value="
                        isInMaintenance(zone.device_type, zone.id, zone.device_proxy_id)
                      "
                      @maintenance-toggle="handleMaintenanceToggle"
                    />
                    <b-form-checkbox class="mt-0" v-model="zone.virtual_bypass" switch
                      @change="handleVirtualBypass(zone, panel.device.id, $event)"> 
                        <label class="form-check-label" for="checkDefault">
                      {{ $t('dmp.virtual_bypass') }}
                    </label>
                    </b-form-checkbox>
                  </b-col>
                  <b-col>
                    <span :class="getStatusClass(zone.status)">
                      {{ zone.status }}
                    </span>
                  </b-col>
                  <b-col>{{ zone.device_type.toUpperCase() }}</b-col>
                  <b-col>{{ zone.text }}</b-col>
                  <b-col>{{ zone.id }}</b-col>
                  <b-col>
                    {{ getReminderMinutes(zone.device_proxy_id) }}
                  </b-col>
                  <b-col>
                    <b-button
                      v-if="isInMaintenance(zone.device_type, zone.id, zone.device_proxy_id)"
                      variant="outline-primary"
                      class="padding-0-1"
                      title="Configurar recordatorio"
                      size="sm"
                      @click="onOpenReminderModal(zone.device_proxy_id)"
                    >
                      <feather-icon icon="ClockIcon" size="16" />
                    </b-button>
                  </b-col>
                </b-row>
              </div>
            </div>
          </b-collapse>
        </div>
      </div>

      <!-- 24-hour independent zones -->
      <div
        v-if="
          panel.devices && panel.devices.zones24h && panel.devices.zones24h.length > 0
        "
      >
        <div
          v-for="zone24h in filteredZones24h"
          :key="`zone24h-${zone24h.id}`"
          class="device-row border-bottom"
        >
          <b-row class="align-items-center p-2">
            <b-col>
              <dmp-zone
                :device="zone24h"
                :trans="trans"
                :loadingToast="loadingToast"
                :updateStatus="updateStatus"
                :maintenance-value="
                  isInMaintenance('zone24h', zone24h.id, zone24h.device_proxy_id)
                "
                @maintenance-toggle="handleMaintenanceToggle"
              />
            </b-col>
            <b-col>
              <span :class="getStatusClass(zone24h.status)">
                {{ zone24h.status }}
              </span>
            </b-col>
            <b-col>{{ zone24h.device_type.toUpperCase() }}</b-col>
            <b-col>{{ zone24h.text }}</b-col>
            <b-col>{{ zone24h.id }}</b-col>
            <b-col>
              {{ getReminderMinutes(zone24h.device_proxy_id) }}
            </b-col>
            <b-col>
              <b-button
                v-if="isInMaintenance('zone24h', zone24h.id, zone24h.device_proxy_id)"
                variant="outline-primary"
                class="padding-0-1"
                title="Configurar recordatorio"
                size="sm"
                @click="onOpenReminderModal(zone24h.device_proxy_id)"
              >
                <feather-icon icon="ClockIcon" size="16" />
              </b-button>
            </b-col>
          </b-row>
        </div>
      </div>

      <!-- Empty state -->
      <div
        v-if="
          !loading &&
          (!panel.devices || (!panel.devices.areas && !panel.devices.zones24h))
        "
        class="text-center text-muted py-3"
      >
        No se encontraron dispositivos.
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-3">
        <b-spinner variant="primary" small />
        <span class="ml-1">Cargando dispositivos...</span>
      </div>

      <!-- Reminder modal -->
      <reminder-modal
        :is-open="isReminderModalOpen"
        @update:reminder-minutes="updateReminderMinutes"
      />
    </div>
  </div>
</template>

<script>
import { ValidationProvider } from "vee-validate";
import {
  BRow,
  BCol,
  BFormInput,
  BCollapse,
  BButton,
  BSpinner,
  BFormCheckbox,
  BBadge,
} from "bootstrap-vue";
import {
  computed,
  ref,
  onMounted,
  getCurrentInstance,
  onBeforeUnmount,
  watch,
} from "@vue/composition-api";
import Swal from "sweetalert2";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import { useToast } from "vue-toastification/composition";
import ToastificationLoadingContent from "@core/components/toastification/ToastificationLoadingContent.vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import DmpArea from "./DmpArea.vue";
import DmpZone from "./DmpZone.vue";
import { mapEventToStatus } from "@/views/dmp/dmp_panels/deviceEventMap.js";
import vSelect from "vue-select";
import axios from "@/libs/axios";
import formValidation from "@core/comp-functions/forms/form-validation";
import ReminderModal from "./ReminderModal.vue";

const mockPanelUsers = [
  {
    panel_uuid: 'b097286f-e9e3-414d-8f64-636e1c95ac59',
    activeUser: true,
    userNumber: "0055",
    userCode: "1234",
    userName: "TEST USER",
    profiles: ["099"],
    value: '0055',
    label: 'TEST USER',
  },
];

export default {
  name: "OperationalDevices",
  components: {
    DmpArea,
    DmpZone,
    ToastificationLoadingContent,
    BRow,
    BCol,
    BFormInput,
    BCollapse,
    BButton,
    BSpinner,
    BFormCheckbox,
    ValidationProvider,
    vSelect,
    ReminderModal,
  },
  props: {
    panelId: {
      type: [Number, String],
      default: 70,
    },
    panelUuid: {
      type: String,
      default: null,
    },
    showFilters: {
      type: Boolean,
      default: true,
    },
    maintenanceDevices: {
      type: Array,
      default: () => [],
    },
    isDisabledForm: {
      type: Boolean,
      default: false,
    },
    formData: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props, { emit }) {
    const { proxy } = getCurrentInstance();

    const { refFormObserver, getValidationState, resetForm } = formValidation({});

    // COMPOSABLES
    const { t, tc } = useI18nUtils();
    const toast = useToast();

    // DATA
    const loading = ref(false);
    const panel = ref({});
    const textFilter = ref("");
    const loadingToast = ref(null);
    const checkStatusInterval = ref(null);
    const expandedAreas = ref([]);
    const maintenanceMode = ref({});

    const panelUsersOptions = ref([]);
    const isLoadingPanelUsers = ref(false);
    const isCreatingCode = ref(false);

    const isReminderModalOpen = ref(false);
    const deviceProxyIdSelected = ref(null);

    // HOOKS
    onMounted(() => {
      if (props.panelId) {
        fetchDevices();
      }
      if (props.panelUuid) {
        getPanelUsers(props.panelUuid);
      }
    });

    onBeforeUnmount(() => {
      clearInterval(checkStatusInterval.value);
    });

    // Watch panelId changes
    watch(
      () => props.panelId,
      (newVal) => {
        if (newVal) {
          fetchDevices();
        }
      }
    );

    // TRANSLATIONS
    const trans = {
      "actions.cancel": t("actions.cancel"),
      "messages.activate.title": t("messages.activate.title"),
      "messages.activate.confirm": t("messages.activate.confirm"),
      "messages.desactivate.title": t("messages.desactivate.title"),
      "messages.desactivate.confirm": t("messages.desactivate.confirm"),
      "messages.loading.title": t("messages.loading.title"),
      "messages.desactivate.error": t("messages.desactivate.error"),
      "messages.activate.error": t("messages.activate.error"),
      "messages.activate.activated_correctly": t("messages.activate.activated_correctly"),
      "messages.desactivate.desactivated_correctly": t(
        "messages.desactivate.desactivated_correctly"
      ),
      "messages.desactivate.desactivated_correctly_confirm": t(
        "messages.desactivate.desactivated_correctly_confirm"
      ),
      "messages.activate.activated_correctly_confirm": t(
        "messages.activate.activated_correctly_confirm"
      ),
      "dmp.panelOffline": t("dmp.panelOffline"),
      "dmp.armPanel": t("dmp.armPanel"),
      "dmp.disarmPanel": t("dmp.disarmPanel"),
      "dmp.arming": t("dmp.arming"),
      "dmp.disarming": t("dmp.disarming"),
      "dmp.syncDevices": t("dmp.syncDevices"),
      "dmp.syncing": t("dmp.syncing"),
      "dmp.syncCorrectly": t("dmp.syncCorrectly"),
      "dmp.syncDevicesSuccess": t("dmp.syncDevicesSuccess"),
      "dmp.syncDevicesError": t("dmp.syncDevicesError"),
      "dmp.devices": t("dmp.devices"),
      "messages.activate.armed_correctly": t("messages.activate.armed_correctly"),
      "messages.activate.armed_correctly_confirm": t(
        "messages.activate.armed_correctly_confirm"
      ),
      "messages.desactivate.disarmed_correctly": t(
        "messages.desactivate.disarmed_correctly"
      ),
      "messages.desactivate.disarmed_correctly_confirm": t(
        "messages.desactivate.disarmed_correctly"
      ),
      "messages.activate.activating": t("messages.activate.activating"),
      "messages.desactivate.deactivating": t("messages.desactivate.deactivating"),
      "messages.processing": t("messages.processing"),
      "dmp.messages.executeCommandConfirm": t("dmp.messages.executeCommandConfirm"),
      "dmp.messages.commandExecutedSuccess": t("dmp.messages.commandExecutedSuccess"),
      "dmp.messages.commandExecutedError": t("dmp.messages.commandExecutedError"),
      "dmp.messages.areYouSure": t("dmp.messages.areYouSure"),
      area: t("dmp.devices.area.title"),
      arm: t("dmp.devices.area.actions.Arm"),
      disarm: t("dmp.devices.area.actions.Disarm"),
      zone: t("dmp.devices.zone.title"),
      Bypass: t("dmp.devices.zone.actions.Bypass"),
      Reset: t("dmp.devices.zone.actions.Reset"),
      zone24h: t("dmp.devices.zone24h.title"),
    };

    // COMPUTED PROPERTIES
    const filteredAreas = computed(() => {
      if (!panel.value.devices || !panel.value.devices.areas) return [];
      if (!textFilter.value) return panel.value.devices.areas;

      return panel.value.devices.areas.filter(
        (area) =>
          [area.id.toString(), area.text.toLowerCase()].some((value) =>
            value.includes(textFilter.value.toLowerCase())
          ) ||
          (area.devices &&
            area.devices.some((zone) =>
              [zone.id.toString(), zone.text.toLowerCase()].some((value) =>
                value.includes(textFilter.value.toLowerCase())
              )
            ))
      );
    });

    const filteredZones24h = computed(() => {
      if (!panel.value.devices || !panel.value.devices.zones24h) return [];
      if (!textFilter.value) return panel.value.devices.zones24h;

      return panel.value.devices.zones24h.filter((zone) =>
        [zone.id.toString(), zone.text.toLowerCase()].some((value) =>
          value.includes(textFilter.value.toLowerCase())
        )
      );
    });

    // METHODS
    async function fetchDevices() {
      loading.value = true;
      try {
        const response = await store.dispatch("dmp_panels/show", props.panelId);
        panel.value = response.data;
        console.log("[OperationalDevices] fetchDevices response:", response);
      } catch (error) {
        console.error("[OperationalDevices] fetchDevices error:", error);
        panel.value = {};
      } finally {
        loading.value = false;
      }
    }

    async function syncDevices() {
      const result = await Swal.fire({
        title: proxy.$t("dmp.syncDevices"),
        text: proxy.$t("dmp.syncPanelWarning"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: proxy.$t("dmp.syncDevices"),
        cancelButtonText: proxy.$t("actions.cancel"),
        customClass: {
          confirmButton: "btn btn-primary text-capitalize",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      });

      if (!result.isConfirmed) return;

      loading.value = true;
      loadingToast.value.show(
        trans["dmp.syncDevices"],
        trans["dmp.syncing"],
        "secondary"
      );

      try {
        await store.dispatch("dmp_panels/syncPanelDevices", panel.value.id);
        await fetchDevices();
        toast({
          component: ToastificationContent,
          props: {
            title: trans["dmp.syncDevicesSuccess"],
            icon: "CheckIcon",
            variant: "success",
          },
        });
      } catch (error) {
        await fetchDevices();
        toast({
          component: ToastificationContent,
          props: {
            title: trans["dmp.syncDevicesError"],
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        loading.value = false;
        loadingToast.value.close();
      }
    }

    async function updateStatus() {
      loading.value = true;
      await fetchDevices();
      loading.value = false;
    }

    const toggleArea = (areaId) => {
      const index = expandedAreas.value.indexOf(areaId);
      if (index > -1) {
        expandedAreas.value.splice(index, 1);
      } else {
        expandedAreas.value.push(areaId);
      }
    };

    const getStatusClass = (status) => {
      const statusClasses = {
        ARMED: "badge badge-success",
        DISARMED: "badge badge-warning",
        NORMAL: "badge badge-info",
        BYPASSED: "badge badge-secondary",
        OFF: "badge badge-dark",
        ON: "badge badge-success",
        OFFLINE: "badge badge-danger",
      };
      return statusClasses[status] || "badge badge-secondary";
    };

    const forwardMaintenanceToggle = (payload) => {
      emit("maintenance-toggle", payload);
    };

    const getInitialMaintenanceState = () => {
      const newSet = new Set();
      props.maintenanceDevices.forEach((device) => {
        if (device.id_device_proxy) newSet.add(device.id_device_proxy);
      });
      return newSet;
    };

    // Centralized maintenance state
    const maintenanceSet = ref(getInitialMaintenanceState());

    const makeKey = (type, id) => `${type}-${id}`;

    const isInMaintenance = (type, id, device_proxy_id) => {
      const keyValue = `${device_proxy_id}`;
      return !!device_proxy_id && maintenanceSet.value.has(device_proxy_id);
      //return maintenanceSet.value.has(makeKey(type, id));
    };

    const getReminderMinutes = (device_proxy_id) => {
      if (!device_proxy_id || !maintenanceSet.value.has(device_proxy_id)) return null;

      const deviceFound = props.maintenanceDevices.find(
        (device) => device.id_device_proxy === device_proxy_id
      );

      const minutes = deviceFound?.new_reminder_minutes ?? deviceFound?.reminder_minutes ?? null;

      const isNotificationSent = deviceFound?.reminder_sent_at ?? null;
    
      return minutes ? `${minutes} min ${isNotificationSent ? '(recordatorio enviado)' : '(recordatorio pendiente)'}` : null;
    };

    const getAreaMaintenanceCount = (area) => {
      if (!area.devices) return 0;
      return area.devices.filter(
        (zone) => !!zone.device_proxy_id && maintenanceSet.value.has(zone.device_proxy_id)
        //maintenanceSet.value.has(makeKey(zone.device_type || 'zone', zone.id))
      ).length;
    };

    // Count all devices and how many are in maintenance
    const totalDevices = computed(() => {
      let count = 0;
      if (panel.value?.devices?.areas) {
        panel.value.devices.areas.forEach((area) => {
          count++; // the area itself
          if (area.devices) count += area.devices.length;
        });
      }
      if (panel.value?.devices?.zones24h) {
        count += panel.value.devices.zones24h.length;
      }
      return count;
    });

    const maintenanceCount = computed(() => {
      return maintenanceSet.value.size;
    });

    const handleMaintenanceToggle = (payload) => {
      console.log({
        msg: "devicesToggle",
        payload,
      });

      const { type, device, value } = payload;
      if (!device.device_proxy_id) return;
      if (type === "area" && (device.devices || []).some((zone) => !zone.device_proxy_id)) return;
      const newSet = new Set(maintenanceSet.value);

      if (type === "area") {
        // Toggle the area itself
        if (value) {
          newSet.add(device.device_proxy_id);
          //newSet.add(makeKey('area', device.id));
        } else {
          newSet.delete(device.device_proxy_id);
          //newSet.delete(makeKey('area', device.id));
        }

        // Cascade to all child zones
        if (device.devices) {
          device.devices.forEach((zone) => {
            const zoneKey = makeKey(zone.device_type || "zone", zone.id);
            if (value) {
              newSet.add(zone.device_proxy_id);
              //newSet.add(zoneKey);
              // Emit for each child zone too
              emit("maintenance-toggle", {
                type: zone.device_type || "zone",
                device: zone,
                value: true,
              });
            } else {
              newSet.delete(zone.device_proxy_id);
              //newSet.delete(zoneKey);
              emit("maintenance-toggle", {
                type: zone.device_type || "zone",
                device: zone,
                value: false,
              });
            }
          });
        }
      } else {
        // It's a zone or zone24h
        if (value) {
          newSet.add(device.device_proxy_id);
          //newSet.add(makeKey(type, device.id));
        } else {
          newSet.delete(device.device_proxy_id);
          //newSet.delete(makeKey(type, device.id));

          // If unchecking a zone, find its parent area and uncheck it
          if (panel.value?.devices?.areas) {
            const parentArea = panel.value.devices.areas.find(
              (area) => area.devices && area.devices.some((z) => z.id === device.id)
            );
            if (parentArea && parentArea.device_proxy_id) {
              newSet.delete(parentArea.device_proxy_id);
              //newSet.delete(makeKey('area', parentArea.id));
              emit("maintenance-toggle", {
                type: "area",
                device: parentArea,
                value: false,
              });
            }
          }
        }
      }

      maintenanceSet.value = newSet;

      // Forward the original event
      emit("maintenance-toggle", payload);
    };

    onMounted(() => {
      fetchDevices();
      if (!window.Echo) return;
      const panelId = props.panelId;
      window.Echo.private(`panel-devices-${panelId}`).listen(
        ".device.updated",
        (event) => {
          handlePanelEvent(event);
        }
      );
    });

    onBeforeUnmount(() => {
      clearInterval(checkStatusInterval.value);

      const panelId = props.panelId;
      window.Echo.leave(`panel-devices-${panelId}`);
    });

    function handlePanelEvent(event) {
      if (!panel.value.devices) return

      let metadata
      try {
        metadata = typeof event.metadata === 'string'
          ? JSON.parse(event.metadata)
          : event.metadata
      } catch { return }

      const { device_type, device_id, active } = metadata
    
      if (active){
        panel.value.devices.panel.status = 'ARMED'
      }else{
        panel.value.devices.panel.status = 'DISARMED'
      }

      const newStatus = mapEventToStatus(metadata)

      if (!newStatus) return

      const normalizeId = (id) => String(parseInt(id, 10))
      const normalizeDoorId = (id) => {
        const num = parseInt(id, 10)
        return num < 10 ? `D0${num}` : `D${num}`
      }

      if (device_type === 'area') {
        const area = (panel.value.devices.areas || [])
          .find(a => normalizeId(a.id) === normalizeId(device_id))
        if (area) area.status = newStatus
        return
      }

      if (device_type === 'zone' || device_type === 'zone24h') {
        let found = false
        for (const area of (panel.value.devices.areas || [])) {
          const zone = (area.devices || [])
            .find(z => normalizeId(z.id) === normalizeId(device_id))
          if (zone) {
            zone.status = newStatus;
            if (metadata.virtual_bypass !== undefined) {
              zone.virtual_bypass = !!metadata.virtual_bypass
            }
            found = true;
            break
          }
        }
        if (!found) {
          const zone24h = (panel.value.devices.zones24h || [])
            .find(z => normalizeId(z.id) === normalizeId(device_id))
          if (zone24h) {
            zone24h.status = newStatus;
            if (metadata.virtual_bypass !== undefined) {
              zone24h.virtual_bypass = !!metadata.virtual_bypass
            }
          }
        }
        return
      }


      if (device_type === 'door') {
        const door = (panel.value.devices.doors || [])
          .find(d => d.id === normalizeDoorId(device_id))
        if (door) door.status = newStatus
        return
      }

      if (device_type === 'output') {
        const output = (panel.value.devices.outputs || [])
          .find(d => normalizeId(d.id) === normalizeId(device_id))
        if (output) output.status = newStatus
        return
      }
    }

    const getPanelUsers = async (panel_uuid) => {
      isLoadingPanelUsers.value = true;

      try {
        const { data } = await axios.get("/v1/dmp/capabilities/get-users", {
          params: {
            technician: true,
            uuid: panel_uuid,
          },
        });

        const users = data.data.map((user) => ({
          ...user,
          panel_uuid,
          value: user.userNumber,
          label: user.userName,
        }));

        panelUsersOptions.value = users;

        if (users.length) {
          formData.value.panel_user = users[0];
        }
      } catch (error) {
      } finally {
        isLoadingPanelUsers.value = false;
      }
    };

    const generateRandomCode = () => {
      const min = 1000;
      const max = 49999;

      const code = Math.floor(Math.random() * (max - min + 1)) + min;

      return code.toString().padStart(5, "0");
    };

    const updatePanelUserCode = async () => {
      const formData = props.formData;

      if (!formData.panel_user) {
        toast({
          component: ToastificationContent,
          props: {
            title: "Selecciona un usuario del panel",
            icon: "CheckIcon",
            variant: "warning",
          },
        });
        return;
      }

      const code = generateRandomCode();
      const user = formData.panel_user;
      const panel_uuid = formData.panel.uuid;

      const userData = {
        uuid: panel_uuid,
        activeUser: true,
        userCode: code,
        userName: user.userName,
        userNumber: user.userNumber,
        profiles: user.profiles,
        description: "",
      };

      isCreatingCode.value = true;

      try {
        await axios.post(`/v1/dmp/capabilities/set-user`, userData);

        emit("form-data-changed", { key: "panel_user_code", value: code });

        toast({
          component: ToastificationContent,
          props: {
            title: "Código generado correctamente",
            icon: "CheckIcon",
            variant: "success",
          },
        });
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title:
              "Error generando código: " + error.response.data.message ??
              "Error desconocido",
            icon: "CheckIcon",
            variant: "danger",
          },
        });
      } finally {
        isCreatingCode.value = false;
      }
    };

    const onOpenReminderModal = (deviceProxyId) => {
      isReminderModalOpen.value = true;
      deviceProxyIdSelected.value = deviceProxyId;

      setTimeout(() => {
        isReminderModalOpen.value = false;
      }, 1000);
    };

    const updateReminderMinutes = (minutes) => {
      emit("update:reminder-minutes", {
        device_proxy_id: deviceProxyIdSelected.value,
        reminder_minutes: minutes,
      });
    };

    const replacePlaceholders = (template, values) => {
      if (!template) return '';
      return template.replace(/%(\w+)%/g, (match, key) => {
        return values.hasOwnProperty(key) ? values[key] : match;
      });
    }


    const handleVirtualBypass = async (zone, camera_id, value) => {

      let placeholders = {
        command: proxy.$t('dmp.virtual_bypass'),
        device: zone.text
      }

      const result = await Swal.fire({
        title: proxy.$t('dmp.virtual_bypass'),
        text: `${replacePlaceholders(proxy.$t('dmp.messages.executeCommandConfirm'), placeholders)}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: proxy.$t('actions.accept'),
        cancelButtonText: proxy.$t('actions.cancel'),
        customClass: {
          confirmButton: 'btn btn-primary text-capitalize',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      });

      if (!result.isConfirmed) {
        zone.virtual_bypass = !value;
        return;
      }

      try {
        //loading.value = true;
        loadingToast.value.show(
          proxy.$t('dmp.virtual_bypass'),
          `${zone.text}`,
          'secondary'
        );

        await store.dispatch('dmp_panels/virtualBypass', {
          panel_id: camera_id,
          device_id: zone.device_id,
          action: 'virtual_bypass',
        });

        toast({
          component: ToastificationContent,
          props: {
            title: `${replacePlaceholders(proxy.$t('dmp.messages.commandExecutedSuccess'), placeholders)}`,
            icon: 'CheckIcon',
            variant: 'success',
          },
        });

        //await fetchDevices();
      } catch (error) {
        zone.virtual_bypass = !value;
        toast({
          component: ToastificationContent,
          props: {
            title: error.response?.data?.message || proxy.$t('dmp.messages.commandExecutedError'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      } finally {
        loading.value = false;
        loadingToast.value.close();
      }
    };

    return {
      replacePlaceholders,
      handleVirtualBypass,
      loading,
      panel,
      textFilter,
      loadingToast,
      updateStatus,
      trans,
      expandedAreas,
      toggleArea,
      getStatusClass,
      filteredAreas,
      filteredZones24h,
      forwardMaintenanceToggle,
      handleMaintenanceToggle,
      isInMaintenance,
      getAreaMaintenanceCount,
      maintenanceCount,
      totalDevices,
      syncDevices,
      //
      panelUsersOptions,
      isCreatingCode,
      isLoadingPanelUsers,
      getValidationState,
      updatePanelUserCode,
      //
      isReminderModalOpen,
      onOpenReminderModal,
      updateReminderMinutes,
      getReminderMinutes,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables/_variables.scss";

.table-fields-devices {
  border-top: 1px solid #ebe9f1;
  border-bottom: 1px solid #ebe9f1;
  background-color: #f3f2f7;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  width: 100%;

  .dark-layout & {
    background-color: $theme-dark-table-header-bg;
    border-color: var(--third-dark);
  }
}

.table-title {
  color: #6e6b7b;
  font-size: 0.857rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 700;

  .dark-layout & {
    color: $theme-dark-headings-color;
  }
}

.device-row {
  border: 1px solid #ebe9f1;
  margin-bottom: 1px;
  width: 100%;

  .dark-layout & {
    background-color: var(--second-dark);
    color: $theme-dark-label-color;
  }
}

.device-row:hover {
  background-color: #f8f9fa;

  .dark-layout & {
    background-color: darken($second-dark, 5%);
  }
}

.area-header {
  background-color: #f3f2f7;
  border: 1px solid #d8d6de;
  transition: all 0.3s ease;

  .dark-layout & {
    background-color: $theme-dark-table-row-bg;
    border-color: var(--third-dark);
  }
}

.area-header:hover {
  background-color: #e7e7e7;
  border-color: #c2bfc7;

  .dark-layout & {
    background-color: $theme-dark-table-hover-bg;
    border-color: var(--third-dark);
  }
}

.area-expanded {
  border-bottom-color: #6e6b7b !important;
  background-color: #e7e7e7;
}

.area-accordion {
  border-radius: 4px;
  overflow: hidden;
  width: 100%;
}

.area-content {
  background-color: #fafafa;
}

.zones-container {
  border-left: 3px solid #d8d6de;
  padding-left: 1rem;
}

.zone-row {
  background-color: #fff;
  margin-left: 0;
}

.zone-row:last-child {
  border-bottom: none;
}

.transition-all {
  transition: all 0.2s ease;
}

.cursor-pointer {
  cursor: pointer;
}

.badge {
  font-size: 0.75rem;
  padding: 0.375rem 0.75rem;
}
</style>
